import { FileAudio, FileIcon, FileText, FileVideo, ImageIcon } from "lucide-react";
import { useState } from "react";

import {
  isBrowserRenderableMediaUrl,
  useOutputMediaContentUrl,
} from "@/queries/vector-store/useVectorStoreFileContent";
import type { MediaPayload } from "@/routes/smart-table/utils/contentType";
import {
  summarizeMultiContent,
  type MultiContentValue,
} from "@/routes/smart-table/utils/multiContent";

export type { MediaPayload };

interface MediaCellPreviewProps {
  media: MediaPayload | MediaPayload[];
  multiContent?: MultiContentValue;
  openDialog?: () => void;
}

export default function MediaCellPreview({
  media,
  multiContent,
  openDialog,
}: MediaCellPreviewProps) {
  const [shouldResolvePreview, setShouldResolvePreview] = useState(false);
  const mediaItems = Array.isArray(media) ? media : [media];
  const firstMedia = mediaItems[0] ?? null;
  const count = mediaItems.length;
  const mediaType = firstMedia?.media_type;
  const rawUrl = firstMedia?.url;
  const storageKey = firstMedia?.storage_key;
  const directUrl = isBrowserRenderableMediaUrl(rawUrl) ? rawUrl : undefined;
  const shouldResolveInlinePreview = count === 1 && mediaType?.startsWith("image");
  const shouldFetchPreviewUrl = shouldResolvePreview && shouldResolveInlinePreview && !directUrl;
  const { previewUrl } = useOutputMediaContentUrl(
    shouldFetchPreviewUrl ? (storageKey ?? null) : null,
  );
  const url = directUrl ?? previewUrl;

  if (!firstMedia) {
    return null;
  }

  const renderIcon = (type?: string, className = "h-3.5 w-3.5") => {
    if (type?.startsWith("image")) {
      return <ImageIcon className={className} />;
    }
    if (type?.startsWith("video")) {
      return <FileVideo className={className} />;
    }
    if (type?.startsWith("audio")) {
      return <FileAudio className={className} />;
    }
    if (type === "pdf" || type === "application/pdf") {
      return <FileText className={className} />;
    }
    return <FileIcon className={className} />;
  };

  const getLabel = (item: MediaPayload) =>
    item.title ||
    (item.media_type
      ? item.media_type.charAt(0).toUpperCase() + item.media_type.slice(1)
      : "Media File");

  let label = getLabel(firstMedia);
  if (count > 1) {
    label = `${count} files`;
  }
  if (multiContent !== undefined) {
    label = summarizeMultiContent(multiContent);
  }

  return (
    <div className="flex h-full w-full items-center justify-center px-1">
      <button
        type="button"
        className="group flex h-7 w-auto max-w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-md bg-muted/60 px-3 transition-colors hover:bg-muted/80"
        onClick={event => {
          event.stopPropagation();
          openDialog?.();
        }}
        onMouseDown={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onDoubleClick={event => event.stopPropagation()}
        onMouseEnter={() => setShouldResolvePreview(true)}
        onFocus={() => setShouldResolvePreview(true)}
      >
        <div className="flex flex-shrink-0 items-center justify-center text-muted-foreground/80 group-hover:text-foreground">
          {(multiContent === undefined || count === 1) && mediaType?.startsWith("image") && url ? (
            <img
              src={url}
              alt={label}
              loading="lazy"
              decoding="async"
              className="h-4 w-4 rounded-sm border border-border/50 object-cover"
            />
          ) : (
            renderIcon(multiContent === undefined ? mediaType : firstMedia?.media_type)
          )}
        </div>
        <span className="truncate text-[13px] font-medium text-foreground/80 group-hover:text-foreground">
          {label}
        </span>
      </button>
    </div>
  );
}
